import React from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import featureNames from '~/shared/constants/featureFlags';
import useHasFeature from '~/shared/utils/useHasFeature';
import {
  mobile,
  desktop,
  useBreakpoint,
  useProductReviews,
  SkeletonLine,
} from '~/techstyle-shared/react-components';

import ReviewAiSummary from '../ReviewAISummary';
import ReviewItem from '../ReviewItem';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ReviewWrapper = styled.div`
  width: 100%;
  display: flex;
  background-color: ${({ theme }) => theme.colors.palette.white};
  padding: 32px;
  box-shadow: inset 0 1px 0 0
    ${({ theme }) => theme.colors.recentReviewsWrapperBoxShadow};
  flex-direction: row;

  ${mobile`
    flex-direction: column;
    padding: 16px 0;
  `}

  ${(props) =>
    props.vertical &&
    css`
      flex-direction: column;
      padding: 16px 0;
    `}

  ${({ blockStyle }) => blockStyle}
`;

const ReviewHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 5px;

  ${desktop`
    width: 220px;
  `}
`;

const ReviewBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const ReviewerName = styled.p`
  font-size: 14px;
  font-weight: bold;

  ${({ reviewerNameStyle }) => reviewerNameStyle}
`;

const ProductInfoLabel = styled.div`
  display: flex;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.darkenGray};

  span {
    &:last-child {
      margin-left: 4px;
    }
  }

  ${({ vertical }) =>
    vertical &&
    css`
      justify-content: space-between;
    `};

  & + & {
    padding-top: 8px;
  }

  ${({ productInfoStyle }) => productInfoStyle};
`;

const StarsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const ReviewContent = styled.span`
  font-size: 14px;

  ${({ contentStyle }) => contentStyle}
`;

const ReviewTitle = styled.p`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;

  ${mobile`
    font-weight: regular;
  `}
`;

const ReviewerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;
`;

export function RecentReviewsLoading({
  blockStyle,
  contentStyle,
  reviewerNameStyle,
  productInfoStyle,
  pageSize = 3,
  vertical,
  ...rest
}) {
  const loadingArray = new Array(pageSize).fill(0);
  const { desktop } = useBreakpoint();
  const shouldRenderProductInfo = desktop && !vertical;

  return (
    <Wrapper {...rest}>
      {loadingArray.map((_, index) => (
        <ReviewWrapper blockStyle={blockStyle} vertical={vertical} key={index}>
          <ReviewHeader>
            <ReviewerWrapper>
              <ReviewerName reviewerNameStyle={reviewerNameStyle}>
                <SkeletonLine width={120} />
              </ReviewerName>
            </ReviewerWrapper>
            {shouldRenderProductInfo && (
              <>
                <ProductInfoLabel productInfoStyle={productInfoStyle}>
                  <SkeletonLine />
                </ProductInfoLabel>

                <ProductInfoLabel productInfoStyle={productInfoStyle}>
                  <SkeletonLine />
                </ProductInfoLabel>
              </>
            )}
          </ReviewHeader>
          <ReviewBody>
            <StarsContainer>
              <SkeletonLine width={180} />
            </StarsContainer>
            <ReviewTitle>
              <SkeletonLine width={100} />
            </ReviewTitle>
            <ReviewContent contentStyle={contentStyle}>
              <SkeletonLine />
            </ReviewContent>
          </ReviewBody>
        </ReviewWrapper>
      ))}
    </Wrapper>
  );
}

RecentReviewsLoading.propTypes = {
  blockStyle: PropTypes.any,
  contentStyle: PropTypes.any,
  pageSize: PropTypes.number,
  productInfoStyle: PropTypes.any,
  reviewerNameStyle: PropTypes.any,
  vertical: PropTypes.bool,
};

export default function RecentReviews({
  blockStyle,
  reviewStarsStyle,
  productInfoStyle,
  productInfoWrapperStyle,
  dateStyle,
  reviewerNameStyle,
  membershipBadges,
  vertical,
  contentStyle,
  showMoreTextProps,
  reviewStarProps,
  aiSummary = '',
  loading,
  pageSize = 10,
  onPictureClick = () => {},
  ...rest
}) {
  const { reviews } = useProductReviews();
  const { enabled: isIncentivizedReviewsEnabled } = useHasFeature(
    featureNames.INCENTIVIZED_REVIEWS_UI
  );

  if (loading) {
    const loadingArray = reviews || new Array(pageSize).fill(0);
    return (
      <RecentReviewsLoading
        blockStyle={blockStyle}
        contentStyle={contentStyle}
        pageSize={loadingArray.length}
        productInfoStyle={productInfoStyle}
        reviewerNameStyle={reviewerNameStyle}
        vertical={vertical}
        {...rest}
      />
    );
  }

  return (
    <Wrapper data-testid="reviews-recent" {...rest}>
      {aiSummary && (
        <ReviewAiSummary
          key="ai-summary"
          aiSummary={aiSummary}
          blockStyle={blockStyle}
          vertical={vertical}
          isModalItem={false}
        />
      )}
      {reviews.map((review, index) => (
        <ReviewItem
          key={review.reviewId || index}
          review={review}
          blockStyle={blockStyle}
          vertical={vertical}
          reviewerNameStyle={reviewerNameStyle}
          productInfoStyle={productInfoStyle}
          reviewStarsStyle={reviewStarsStyle}
          dateStyle={dateStyle}
          contentStyle={contentStyle}
          productInfoWrapperStyle={productInfoWrapperStyle}
          showMoreTextProps={showMoreTextProps}
          reviewStarProps={reviewStarProps}
          isIncentivizedReviewsEnabled={isIncentivizedReviewsEnabled}
          isModalItem={false}
        />
      ))}
    </Wrapper>
  );
}

RecentReviews.propTypes = {
  /**
   * AI-generated summary of all of the individual reviews.
   */
  aiSummary: PropTypes.string,

  /**
   * Styles to pass to the block container element. It will be included along with the
   * base styles and can be anything styled-components supports in its tagged
   * template literals, including strings, objects, and functions.
   */
  blockStyle: PropTypes.any,

  /**
   * Styles to pass to the review content element. It will be included along with the
   * base styles and can be anything styled-components supports in its tagged
   * template literals, including strings, objects, and functions.
   */
  contentStyle: PropTypes.any,

  /**
   * Styles to pass to the date element. It will be included along with the
   * base styles and can be anything styled-components supports in its tagged
   * template literals, including strings, objects, and functions.
   */
  dateStyle: PropTypes.any,

  /* Prop to control if the data is still loading. Will render a skeleton of the data */
  loading: PropTypes.bool,

  /**
   *  Object containing components to be selected by the membershipLevel key
   */
  membershipBadges: PropTypes.object,

  /**
   * A callback that's triggered once review picture is clicked.
   * Passes revivers name and image sources as an argument
   */
  onPictureClick: PropTypes.func,

  /**
   *  Size of the page of reviews that is being loaded. Will be used the correct
   * expected amount of skeletons
   */
  pageSize: PropTypes.number,

  /**
   * Styles to pass to the review stars element. It will be included along with the
   * base styles and can be anything styled-components supports in its tagged
   * template literals, including strings, objects, and functions.
   */
  productInfoStyle: PropTypes.any,

  /**
   * Styles to pass to ProductInfoWrapper component, this appears when `vertical` is enabled.
   * By default this section is hidden for mobile.
   *  */
  productInfoWrapperStyle: PropTypes.any,

  // Review content to render in the block
  review: PropTypes.object,

  /**
   * Prop that will be spread on the review stars.
   * Accepts any of the props that can be passed to ReviewStars component
   */
  reviewStarProps: PropTypes.object,

  /**
   * Styles to pass to the review stars element. It will be included along with the
   * base styles and can be anything styled-components supports in its tagged
   * template literals, including strings, objects, and functions.
   */
  reviewStarsStyle: PropTypes.any,
  /**
   * Styles to pass to the review stars element. It will be included along with the
   * base styles and can be anything styled-components supports in its tagged
   * template literals, including strings, objects, and functions.
   */
  reviewerNameStyle: PropTypes.any,

  /**
   * Any props that the component ShowMoreText accepts
   */
  showMoreTextProps: PropTypes.any,

  /**
   * Boolean that controls if flex-direction is column and renders or not product info.
   * Supposed to be used when using the recent reviews block inside the side drawer, for example.
   */
  vertical: PropTypes.bool,
};
